import { L } from '@lib';
import { InventoryLookup, InventoryService } from '@services';
import { message, Select, Tag } from 'antd';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

interface IProps {
  onSelect?: (item: InventoryLookup) => void;
  placeholder?: string;
  size?: 'small' | 'middle' | 'large';
}

export const InventorySelect: React.FC<IProps> = ({ onSelect, placeholder, size }) => {
  const [items, setItems] = useState<InventoryLookup[]>([]);

  const fetchItems = async (search: string) => {
    try {
      const result = await InventoryService.lookup(search);
      setItems(result);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchItems('');
  }, []);

  const handleSearch = useMemo(
    () => debounce((text: string) => fetchItems(text || ''), 400),
    []
  );

  const handleSelect = (value: string) => {
    const item = items.find((i) => i.id === value);
    if (!item) return;
    if (item.quantity <= 0) {
      message.warning(L('{0} is out of stock — added anyway').replace('{0}', item.name));
    } else if (item.quantity < 2) {
      message.info(L('Low stock — only {0} left').replace('{0}', String(item.quantity)));
    }
    onSelect?.(item);
  };

  const options = useMemo(() => {
    return items.map((item) => {
      const parts: string[] = [item.name];
      if (item.supplierName) parts.push(`Supplier: ${item.supplierName}`);
      if (item.reference) parts.push(`Ref: ${item.reference}`);
      parts.push(`Price: £${item.sellPrice?.toFixed(2)}`);
      const text = parts.join(' | ');
      const stockTag =
        item.quantity <= 0 ? (
          <Tag color="red" style={{ marginLeft: 6 }}>Out of stock</Tag>
        ) : item.quantity < 2 ? (
          <Tag color="orange" style={{ marginLeft: 6 }}>Low: {item.quantity}</Tag>
        ) : (
          <Tag style={{ marginLeft: 6 }}>In stock: {item.quantity}</Tag>
        );
      return {
        label: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span>{text}</span>
            {stockTag}
          </span>
        ),
        value: item.id,
      };
    });
  }, [items]);

  return (
    <Select
      allowClear
      showSearch
      notFoundContent={null}
      filterOption={false}
      style={{ width: '100%' }}
      size={size}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      placeholder={placeholder || L('Search inventory...')}
      placement="topLeft"
      getPopupContainer={(trigger: any) => trigger.parentElement}
    />
  );
};
